"use client";

import { useLayoutEffect, type RefObject } from "react";

export function useVideoViewport(frameRef: RefObject<HTMLElement | null>, episodeId?: string) {
  useLayoutEffect(() => {
    const player = frameRef.current;
    if (!player) return;
    let pending = 0;
    let disposed = false;
    function update() {
      pending = 0;
      if (disposed || !player || document.fullscreenElement?.contains(player)) return;
      const viewportHeight = Math.floor(Math.min(window.innerHeight, window.visualViewport?.height ?? window.innerHeight));
      const bottomPadding = player.parentElement ? parseFloat(getComputedStyle(player.parentElement).paddingBottom) || 0 : 0;

      const offset = Math.ceil(Math.max(0, player.getBoundingClientRect().top + window.scrollY) + bottomPadding + 16);
      player.style.setProperty("--video-viewport-height", `${viewportHeight}px`);
      player.style.setProperty("--video-page-offset", `${offset}px`);
    }
    function schedule() {
      if (!disposed && !pending) pending = requestAnimationFrame(update);
    }
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);

    for (let element = player.parentElement; element; element = element.parentElement) observer?.observe(element);
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    document.addEventListener("fullscreenchange", schedule);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      document.removeEventListener("fullscreenchange", schedule);
      if (pending) cancelAnimationFrame(pending);
      player.style.removeProperty("--video-viewport-height");
      player.style.removeProperty("--video-page-offset");
    };
  }, [frameRef, episodeId]);
}
